import React from 'react';
import { motion } from 'framer-motion';
import { FaMicrophone, FaStop } from 'react-icons/fa';

interface VoiceButtonProps {
  isListening: boolean;
  isProcessing: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  label?: string;
}

/**
 * Glassmorphic mic button. Pulses red while listening and switches to a stop
 * icon so the user can always break out of a listening session.
 */
const VoiceButton: React.FC<VoiceButtonProps> = ({
  isListening,
  isProcessing,
  disabled = false,
  onClick,
  className = '',
  label = 'Voice input',
}) => {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={isListening ? 'Stop listening' : label}
      title={isListening ? 'Stop listening' : label}
      className={`relative w-12 h-12 rounded-full flex items-center justify-center shrink-0
        border backdrop-blur-md transition-colors
        ${
          isListening
            ? 'bg-rose-500/90 border-rose-400 text-white shadow-[0_0_20px_rgba(244,63,94,0.6)]'
            : 'bg-white/70 dark:bg-white/10 border-gray-200 dark:border-white/15 text-primary dark:text-primary hover:bg-primary/10 hover:shadow-[0_0_16px_rgba(108,99,255,0.45)]'
        }
        disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
      whileHover={{ scale: disabled ? 1 : 1.08 }}
      whileTap={{ scale: 0.9 }}
    >
      {isListening && (
        <>
          <span className="absolute inset-0 rounded-full bg-rose-500/40 animate-ripple" />
          <span className="absolute inset-0 rounded-full bg-rose-500/30 animate-ripple" style={{ animationDelay: '0.5s' }} />
        </>
      )}
      <motion.span
        animate={isListening ? { scale: [1, 1.15, 1] } : {}}
        transition={{ duration: 0.8, repeat: isListening ? Infinity : 0 }}
        className="relative z-10"
      >
        {isListening ? (
          <FaStop className="text-lg" />
        ) : (
          <FaMicrophone className="text-lg" />
        )}
      </motion.span>
      {isProcessing && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-secondary rounded-full animate-pulse" />
      )}
    </motion.button>
  );
};

export default VoiceButton;
